import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { jourCourt } from "@/lib/dates";
import { ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";

const TYPES = [
  { key: "travaille", label: "Travaillé", color: "#002FA7" },
  { key: "repos", label: "Repos", color: "#868E96" },
  { key: "conge", label: "Congé", color: "#0066FF" },
  { key: "conge_paye", label: "Congé payé", color: "#059669" },
  { key: "absence_maladie", label: "Maladie", color: "#E60000" },
  { key: "jour_ferie", label: "Jour férié", color: "#FFD700" },
];

function startOfWeek(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}
function fmt(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }

export default function ChefPlanning() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState("week"); // week | month
  const [anchor, setAnchor] = useState(new Date());
  const [allSalaries, setAllSalaries] = useState([]);
  const [sites, setSites] = useState(null); // null = multi-sites désactivé
  const [entries, setEntries] = useState([]);
  const [closedDays, setClosedDays] = useState([]);
  const [editing, setEditing] = useState(null); // { salarie_id, date }
  const [loading, setLoading] = useState(false);
  const siteFilter = searchParams.get("site") || "all";
  const setSiteFilter = (v) => { const p = new URLSearchParams(searchParams); if (v === "all") p.delete("site"); else p.set("site", v); setSearchParams(p, { replace: true }); };

  useEffect(() => { api.get("/chef/sites").then((r) => setSites(r.data || [])).catch(() => setSites(null)); }, []);
  const salaries = useMemo(() => siteFilter === "all" ? allSalaries : siteFilter === "none" ? allSalaries.filter((s) => !s.site_id) : allSalaries.filter((s) => s.site_id === siteFilter), [allSalaries, siteFilter]);

  const { start, end, dates } = useMemo(() => {
    let start, end;
    if (viewMode === "week") {
      start = startOfWeek(anchor);
      end = addDays(start, 6);
    } else {
      start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    }
    const dates = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) dates.push(new Date(d));
    return { start, end, dates };
  }, [viewMode, anchor]);

  const load = async () => {
    setLoading(true);
    try {
      const isSite = siteFilter !== "all" && siteFilter !== "none";
      const [sRes, pRes, cRes] = await Promise.all([
        api.get("/chef/salaries"),
        api.get("/chef/planning", { params: { date_start: fmt(start), date_end: fmt(end) } }),
        isSite ? api.get(`/chef/etablissements/${siteFilter}/closed-days`, { params: { date_start: fmt(start), date_end: fmt(end) } }).catch(() => ({ data: { closed_days: [] } })) : Promise.resolve({ data: { closed_days: [] } }),
      ]);
      setAllSalaries(sRes.data);
      setEntries(pRes.data);
      setClosedDays(cRes.data.closed_days || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [viewMode, anchor, siteFilter]);

  const entryAt = (sid, d) => entries.find((e) => e.salarie_id === sid && e.date === fmt(d));
  const isClosed = (d) => closedDays.includes(fmt(d));
  const conflicts = useMemo(() => entries.filter((e) => e.type === "travaille" && closedDays.includes(e.date) && salaries.some((s) => s.id === e.salarie_id)).length, [entries, closedDays, salaries]);

  const save = async (form) => {
    try {
      await api.post("/chef/planning", form);
      toast.success("Planning mis à jour");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const shift = (dir) => {
    if (viewMode === "week") setAnchor(addDays(anchor, dir * 7));
    else {
      const c = new Date(anchor);
      c.setMonth(c.getMonth() + dir);
      setAnchor(c);
    }
  };

  const title = viewMode === "week"
    ? `${start.toLocaleDateString("fr-FR")} → ${end.toLocaleDateString("fr-FR")}`
    : anchor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Planning"
        title="Plannings"
        sub="Vue semaine ou mois. Cliquez sur une cellule pour définir le type de journée."
        right={
          <div className="flex items-center gap-2 flex-wrap">
            {sites !== null && (
              <Select value={siteFilter} onValueChange={setSiteFilter}>
                <SelectTrigger data-testid="planning-site-filter" className="w-52 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les établissements</SelectItem>
                  <SelectItem value="none">Non affectés</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger data-testid="planning-view-toggle" className="w-32 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Semaine</SelectItem>
                <SelectItem value="month">Mois</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => shift(-1)} data-testid="planning-prev" className="h-10"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="h-10 px-4 border border-[#DEE2E6] bg-white flex items-center text-sm font-semibold min-w-[240px] justify-center" data-testid="planning-range">{title}</div>
            <Button variant="outline" onClick={() => shift(1)} data-testid="planning-next" className="h-10"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        }
      />
      <div className="p-6 lg:p-10">
        {conflicts > 0 && (
          <div className="flex items-center gap-2 text-xs font-semibold bg-[#FEF2F2] border border-[#FECACA] text-[#B91C1C] px-3 py-2 mb-4" data-testid="planning-closed-alert">
            <AlertTriangle className="h-4 w-4 shrink-0" /> {conflicts} créneau{conflicts > 1 ? "x" : ""} travaillé{conflicts > 1 ? "s" : ""} planifié{conflicts > 1 ? "s" : ""} un jour où l'établissement est fermé.
          </div>
        )}
        <div className="flex gap-3 mb-4 flex-wrap">
          {TYPES.map((t) => (
            <div key={t.key} className="flex items-center gap-2 text-xs font-semibold">
              <span className="h-3 w-3" style={{ background: t.color }} />{t.label}
            </div>
          ))}
        </div>
        <div className="bg-white border border-[#DEE2E6] overflow-x-auto" data-testid="planning-grid">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#F8F9FA] border-b border-[#DEE2E6]">
                <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.2em] font-semibold border-r border-[#DEE2E6] sticky left-0 bg-[#F8F9FA] z-10 min-w-[180px]">Salarié</th>
                {dates.map((d) => (
                  <th key={d.toISOString()} className={`px-2 py-2 text-center border-r border-[#DEE2E6] text-xs min-w-[70px] ${isClosed(d) ? "bg-[#FEF2F2] text-[#B91C1C]" : ""}`} data-testid={isClosed(d) ? `planning-closed-day-${fmt(d)}` : undefined}>
                    <div className="uppercase tracking-wider font-semibold">{jourCourt(d)}</div>
                    <div className="font-mono">{d.getDate()}/{d.getMonth() + 1}</div>
                    {isClosed(d) && <div className="text-[9px] uppercase tracking-wider font-bold">Fermé</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {salaries.map((s) => (
                <tr key={s.id} className="border-b border-[#DEE2E6]">
                  <td className="px-3 py-2 border-r border-[#DEE2E6] sticky left-0 bg-white z-10 font-semibold">
                    {s.prenom} {s.nom}
                    <div className="text-xs text-[#868E96] font-normal">{s.poste}</div>
                  </td>
                  {dates.map((d) => {
                    const e = entryAt(s.id, d);
                    const t = TYPES.find((x) => x.key === e?.type);
                    return (
                      <td key={d.toISOString()} className={`border-r border-[#DEE2E6] p-0 text-center ${isClosed(d) ? "bg-[#FEF2F2]" : ""}`}>
                        <button
                          onClick={() => setEditing({ salarie_id: s.id, date: fmt(d), current: e })}
                          data-testid={`cell-${s.code}-${fmt(d)}`}
                          className="w-full h-14 hover:bg-[#F8F9FA] flex items-center justify-center transition-colors relative"
                          style={t ? { backgroundColor: t.color, color: t.color === "#FFD700" ? "#0A0A0A" : "white" } : {}}
                        >
                          {e ? (
                            <div className="text-xs font-semibold">
                              {e.type === "travaille" ? (e.horaire_debut ? `${e.horaire_debut}-${e.horaire_fin}` : "T") : t?.label.charAt(0)}
                            </div>
                          ) : <span className="text-[#DEE2E6]">+</span>}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {salaries.length === 0 && (
                <tr><td colSpan={dates.length + 1} className="p-8 text-center text-sm text-[#868E96]">Aucun salarié. Ajoutez-en depuis la page Salariés.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditModal editing={editing} onClose={() => setEditing(null)} onSave={save} />
      )}
    </div>
  );
}

function EditModal({ editing, onClose, onSave }) {
  const [type, setType] = useState(editing.current?.type || "travaille");
  const [debut, setDebut] = useState(editing.current?.horaire_debut || "09:00");
  const [fin, setFin] = useState(editing.current?.horaire_fin || "17:00");
  const submit = () => {
    onSave({
      salarie_id: editing.salarie_id,
      date: editing.date,
      type,
      horaire_debut: type === "travaille" ? debut : null,
      horaire_fin: type === "travaille" ? fin : null,
    });
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white border border-[#0A0A0A] w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="planning-edit-modal">
        <h3 className="font-display text-xl font-black tracking-tight mb-4">Éditer la journée du {editing.date}</h3>
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2">Type</div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="edit-type" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {type === "travaille" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2">Début</div>
                <Input type="time" value={debut} onChange={(e) => setDebut(e.target.value)} data-testid="edit-debut" className="h-11" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2">Fin</div>
                <Input type="time" value={fin} onChange={(e) => setFin(e.target.value)} data-testid="edit-fin" className="h-11" />
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
            <Button onClick={submit} data-testid="edit-save" className="flex-1 bg-[#002FA7] hover:bg-[#002175] text-white">Enregistrer</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
